const LEADING_PLUS_PATTERN = /^\+/

export type OddsFormat = 'american' | 'decimal'

export function convertDecimalToAmerican(decimalOdds: number): string | null {
  if (decimalOdds <= 1) return null
  if (Math.abs(decimalOdds - 2.0) < 0.0001) return '+100'
  if (decimalOdds > 2.0) {
    const americanOdds = Math.floor((decimalOdds - 1) * 100)
    return `+${americanOdds}`
  } else {
    const rawOdds = -100 / (decimalOdds - 1)
    return `${Math.ceil(rawOdds)}`
  }
}

// Sportsbook decimal odds are conventionally shown to two decimal places
// (e.g. 2.50, 1.91); anything at or below 1 carries no payout and is invalid.
function formatDecimalDisplay(decimalOdds: number): string | null {
  if (!Number.isFinite(decimalOdds) || decimalOdds <= 1) return null
  return decimalOdds.toFixed(2)
}

// American strings/numbers are always mathematically convertible to decimal;
// this is the only safe fallback path when no exact decimal field exists.
function americanToDecimal(americanValue: string | number): number | null {
  const americanNum = typeof americanValue === 'number' ? americanValue : parseAmericanOdds(americanValue)
  if (americanNum == null || !Number.isFinite(americanNum) || americanNum === 0) return null
  return americanNum > 0 ? americanNum / 100 + 1 : 100 / Math.abs(americanNum) + 1
}

function americanToDecimalDisplay(americanValue: string | number): string | null {
  const decimal = americanToDecimal(americanValue)
  return decimal === null ? null : formatDecimalDisplay(decimal)
}

export function parseAmericanOdds(oddsStr: string | null): number | null {
  if (!oddsStr) return null
  if (oddsStr.toUpperCase() === 'EVEN') return 100
  const num = parseInt(oddsStr.replace('+', ''), 10)
  return isNaN(num) ? null : num
}

/**
 * The decimal price behind a displayed odds string, in either format, so two
 * prices compare the same way whichever format the host shows: decimal
 * strings always carry a point ("2.50"), American ones never do ("+150").
 */
export function displayedOddsValue(oddsStr: string | null | undefined): number | null {
  if (!oddsStr) return null
  if (!oddsStr.includes('.')) return americanToDecimal(oddsStr)
  const decimal = Number(oddsStr)
  return Number.isFinite(decimal) && decimal > 1 ? decimal : null
}

/**
 * The decimal price of an odds value as `formatOdds` reads it: an exact
 * decimal field wins, signed strings are American, bare numbers decimal.
 */
export function decimalOddsOf(
  odds: string | number | Record<string, unknown> | null | undefined,
): number | null {
  if (odds == null) return null
  if (typeof odds === 'object') {
    const decimal = Number(odds.decimal ?? odds.dec)
    if (Number.isFinite(decimal) && decimal > 1) return decimal
    const american = odds.american ?? odds.us
    return typeof american === 'string' || typeof american === 'number' ? americanToDecimal(american) : null
  }
  const str = String(odds).trim()
  if (str.toUpperCase() === 'EVEN' || str.startsWith('+') || str.startsWith('-')) return americanToDecimal(str)
  const decimal = Number(str)
  return Number.isFinite(decimal) && decimal > 1 ? decimal : null
}

/** The product of the legs' decimal prices; null unless every leg has one. */
export function combinedDecimalOdds(decimals: ReadonlyArray<number | null | undefined>): number | null {
  if (decimals.length === 0) return null
  let product = 1
  for (const decimal of decimals) {
    if (decimal == null || !Number.isFinite(decimal) || decimal <= 1) return null
    product *= decimal
  }
  return product
}

// Keep odds formatting in lockstep with the plain-JS displayOdds copy in apps/shared/pkg/prepacks/templateRendering.js.
export function formatOdds(
  odds: string | number | Record<string, unknown> | null | undefined,
  format: OddsFormat = 'american',
): string {
  if (odds == null) return '--'
  // Handle odds object from denorm payload: { decimal, american }
  if (typeof odds === 'object') {
    if (format === 'decimal') {
      // Prefer an exact decimal field; only fall back to converting the
      // American field when no decimal value is present.
      const decimal = odds.decimal ?? odds.dec
      if (decimal != null) {
        const numeric = Number(decimal)
        const normalized = Number.isFinite(numeric) ? formatDecimalDisplay(numeric) : null
        if (normalized) return normalized
      }
      const american = odds.american ?? odds.us
      if (typeof american === 'string' || typeof american === 'number') {
        const normalized = americanToDecimalDisplay(american)
        if (normalized) return normalized
      }
      return '--'
    }
    if (odds.american) return String(odds.american)
    if (odds.us) return String(odds.us)
    const decimal = odds.decimal || odds.dec
    if (decimal) {
      const numeric = Number(decimal)
      return (Number.isFinite(numeric) && convertDecimalToAmerican(numeric)) || '--'
    }
    return '--'
  }
  const str = String(odds)
  if (format === 'decimal') {
    // A signed string is an American-only source; converting it via
    // parseFloat would misread "+150" as the decimal value 150, so it must
    // go through the American→decimal formula instead.
    if (str.startsWith('+') || str.startsWith('-')) {
      return americanToDecimalDisplay(str) || str
    }
    const num = parseFloat(str)
    if (isNaN(num)) return str
    return formatDecimalDisplay(num) || str
  }
  if (str.startsWith('+') || str.startsWith('-')) return str
  const num = parseFloat(str)
  if (isNaN(num)) return str
  const american = convertDecimalToAmerican(num)
  return american || str
}

export function calculateParlayOdds(odds: string[]): string {
  if (!odds || odds.length === 0) return ''
  if (odds.length === 1) {
    return odds[0].startsWith('+') || odds[0].startsWith('-') ? odds[0] : '+' + odds[0]
  }

  let payout = 1
  for (const oddStr of odds) {
    const cleanOdd = parseInt(oddStr.replace(LEADING_PLUS_PATTERN, ''), 10)
    if (isNaN(cleanOdd)) continue
    if (cleanOdd > 0) {
      payout *= cleanOdd / 100 + 1
    } else if (cleanOdd < 0) {
      payout *= 100 / Math.abs(cleanOdd) + 1
    }
  }

  if (payout <= 1) return '--'

  if (payout >= 2.0) {
    return '+' + Math.floor((payout - 1) * 100)
  } else {
    return Math.floor(-100 / (payout - 1)).toString()
  }
}

export function calculatePayout(odds: string, wager: number = 10): string {
  const parsed = parseAmericanOdds(odds)
  if (parsed == null) return '--'
  let profit: number
  if (parsed > 0) {
    profit = (parsed / 100) * wager
  } else {
    profit = (100 / Math.abs(parsed)) * wager
  }
  return (wager + profit).toFixed(2)
}
